import type { BeadColor } from '../catalog/types';
import type { RgbColor } from '../color/types';
import type { Grid } from './types';
import { findNearestIndex } from './find-nearest';

/**
 * Réduction du nombre de couleurs (Lot P2 du pivot e-commerce).
 *
 * Objectif produit : chaque couleur d'un kit = un sachet à trier = du coût
 * atelier. Les presets du tunnel plafonnent donc le nombre de couleurs
 * (cf. CDC §4.5) : on choisit les K couleurs de la palette HAMA qui
 * représentent le mieux L'IMAGE, puis la quantification est contrainte à ce
 * sous-ensemble.
 *
 * Algorithme : median-cut (Heckbert 1982) sur un échantillon des pixels de
 * la grille redimensionnée — l'espace est découpé en K boîtes le long du
 * canal de plus grande amplitude, la moyenne de chaque boîte est projetée
 * sur la couleur HAMA la plus proche (CIEDE2000), et les doublons sont
 * fusionnés. Le CDC v0.4 §3 prévoyait median-cut/k-means : c'est ici.
 *
 * Retourne des INDICES dans la palette complète (ordre canonique croissant),
 * jamais une sous-palette : le `Pattern.paletteColorIds` reste la palette
 * canonique complète, ce qui préserve la compatibilité éditeur/persistance.
 */

/** Plafond d'échantillonnage : au-delà, on stride (déterministe). */
const MAX_SAMPLES = 16384;

type Box = {
  /** Indices dans `samples`. */
  start: number;
  end: number; // exclusif
};

export function reduceToAllowedIndices(
  grid: Grid,
  palette: ReadonlyArray<BeadColor>,
  maxColors: number,
): number[] {
  if (palette.length === 0) {
    throw new Error('Réduction impossible : palette vide.');
  }
  if (!Number.isFinite(maxColors) || maxColors < 1) {
    throw new Error(`maxColors invalide : ${maxColors} (≥ 1 attendu).`);
  }
  if (maxColors >= palette.length) {
    return palette.map((_, i) => i);
  }

  // --- Échantillonnage déterministe (stride) -----------------------------
  const total = grid.pixels.length;
  const stride = Math.max(1, Math.floor(total / MAX_SAMPLES));
  const samples: RgbColor[] = [];
  for (let i = 0; i < total; i += stride) {
    const p = grid.pixels[i];
    if (p !== undefined) samples.push(p);
  }
  if (samples.length === 0) {
    throw new Error('Réduction impossible : grille vide.');
  }

  // --- Median-cut ---------------------------------------------------------
  // On travaille sur une copie mutable (les boîtes trient leur segment).
  const work = samples.slice();
  const boxes: Box[] = [{ start: 0, end: work.length }];

  while (boxes.length < maxColors) {
    // Boîte à découper : la plus grande amplitude (canal max), départage
    // par population. Les boîtes mono-pixel ne sont pas découpables.
    let bestBox = -1;
    let bestRange = -1;
    let bestChannel: keyof RgbColor = 'r';
    for (let b = 0; b < boxes.length; b++) {
      const box = boxes[b];
      if (box === undefined || box.end - box.start < 2) continue;
      const { channel, range } = widestChannel(work, box);
      if (range > bestRange) {
        bestRange = range;
        bestBox = b;
        bestChannel = channel;
      }
    }
    if (bestBox === -1 || bestRange === 0) break; // plus rien à découper

    const box = boxes[bestBox];
    if (box === undefined) break;
    const segment = work.slice(box.start, box.end);
    segment.sort((a, b) => a[bestChannel] - b[bestChannel]);
    for (let i = 0; i < segment.length; i++) {
      const s = segment[i];
      if (s !== undefined) work[box.start + i] = s;
    }
    const mid = box.start + Math.floor((box.end - box.start) / 2);
    const right: Box = { start: mid, end: box.end };
    box.end = mid;
    boxes.push(right);
  }

  // --- Projection sur la palette + dédoublonnage --------------------------
  const chosen = new Set<number>();
  for (const box of boxes) {
    const mean = boxMean(work, box);
    chosen.add(findNearestIndex(mean, palette));
  }

  // Ordre canonique croissant : stabilité du rendu et des tests.
  return [...chosen].sort((a, b) => a - b);
}

function widestChannel(
  pixels: ReadonlyArray<RgbColor>,
  box: Box,
): { channel: keyof RgbColor; range: number } {
  let minR = 255, maxR = 0, minG = 255, maxG = 0, minB = 255, maxB = 0;
  for (let i = box.start; i < box.end; i++) {
    const p = pixels[i];
    if (p === undefined) continue;
    if (p.r < minR) minR = p.r;
    if (p.r > maxR) maxR = p.r;
    if (p.g < minG) minG = p.g;
    if (p.g > maxG) maxG = p.g;
    if (p.b < minB) minB = p.b;
    if (p.b > maxB) maxB = p.b;
  }
  const rangeR = maxR - minR;
  const rangeG = maxG - minG;
  const rangeB = maxB - minB;
  if (rangeG >= rangeR && rangeG >= rangeB) return { channel: 'g', range: rangeG };
  if (rangeR >= rangeB) return { channel: 'r', range: rangeR };
  return { channel: 'b', range: rangeB };
}

function boxMean(pixels: ReadonlyArray<RgbColor>, box: Box): RgbColor {
  let r = 0, g = 0, b = 0;
  const n = Math.max(1, box.end - box.start);
  for (let i = box.start; i < box.end; i++) {
    const p = pixels[i];
    if (p === undefined) continue;
    r += p.r;
    g += p.g;
    b += p.b;
  }
  return {
    r: Math.round(r / n),
    g: Math.round(g / n),
    b: Math.round(b / n),
  };
}
